import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, CheckCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Card, Field, TextInput, ErrorBox, btnPrimary, btnSecondary, StatusBadge, fmtDate } from "@/components/common/Form";
import { EmptyState, LoadingState } from "@/components/common/States";

export function SecurityPage() {
  const { user, setUser } = useStore();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sessions, setSessions] = useState([]);
  const loadSessions = useCallback(() => api.get("/auth/sessions").then((r) => setSessions(r.data)).catch(() => {}), []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const changePw = async (e) => {
    e.preventDefault(); setError("");
    if (pw.new_password !== pw.confirm) return setError("New passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated"); setPw({ current_password: "", new_password: "", confirm: "" }); loadSessions();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const resend = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendOtp = async () => {
    try { await api.post("/auth/phone/send-otp"); setOtpSent(true); toast.success("OTP sent to your phone"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const verifyOtp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/phone/verify-otp", { otp });
      const r = await api.get("/auth/me"); setUser(r.data.user); setOtpSent(false); setOtp(""); toast.success("Phone verified");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const revokeOthers = async () => { await api.post("/auth/sessions/revoke-others"); toast.success("Other sessions signed out"); loadSessions(); };
  const set = (k) => (e) => setPw({ ...pw, [k]: e.target.value });

  return (
    <div className="space-y-6">
      <Card title="Verification" subtitle="Verified contact details keep your account secure." testId="verification-card">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between"><span className="font-semibold">Email</span><StatusBadge value={user.email_verified ? "VERIFIED" : "PENDING_VERIFICATION"} testId="security-email-status" /></div>
            <p className="text-stone-500 mt-1">{user.email}</p>
            {!user.email_verified && <button onClick={resend} data-testid="resend-verification" className={`${btnSecondary} mt-3`}>Resend verification email</button>}
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between"><span className="font-semibold">Phone</span><StatusBadge value={!user.phone ? "NOT_SUBMITTED" : user.phone_verified ? "VERIFIED" : "PENDING_VERIFICATION"} testId="security-phone-status" /></div>
            <p className="text-stone-500 mt-1">{user.phone || "Add a phone number in your profile."}</p>
            {user.phone && !user.phone_verified && !otpSent && <button onClick={sendOtp} data-testid="send-otp" className={`${btnSecondary} mt-3`}>Send OTP</button>}
            {otpSent && (
              <form onSubmit={verifyOtp} className="mt-3 flex gap-2">
                <input data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} placeholder="6-digit OTP" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm outline-none" />
                <button data-testid="verify-otp" className={btnPrimary}>Verify</button>
              </form>
            )}
          </div>
        </div>
      </Card>
      <Card title="Change password" subtitle="8+ characters with uppercase, lowercase and a number." testId="password-card">
        <form onSubmit={changePw} className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3"><ErrorBox message={error} testId="password-error" /></div>
          <Field label="Current password"><TextInput testId="pw-current" type="password" required value={pw.current_password} onChange={set("current_password")} /></Field>
          <Field label="New password"><TextInput testId="pw-new" type="password" required value={pw.new_password} onChange={set("new_password")} /></Field>
          <Field label="Confirm new password"><TextInput testId="pw-confirm" type="password" required value={pw.confirm} onChange={set("confirm")} /></Field>
          <div className="sm:col-span-3"><button disabled={saving} data-testid="pw-save" className={btnPrimary}>{saving ? "Updating..." : "Update password"}</button></div>
        </form>
      </Card>
      <Card title="Active sessions" subtitle="Devices currently signed in to your account." testId="sessions-card"
        action={sessions.length > 1 && <button onClick={revokeOthers} data-testid="revoke-others" className={btnSecondary}>Sign out other devices</button>}>
        <ul className="divide-y divide-border text-sm">
          {sessions.map((s) => (
            <li key={s.sid} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0"><div className="font-medium truncate">{s.user_agent || "Unknown device"}</div><div className="text-xs text-stone-500">IP {s.ip || "—"} · Last active {fmtDate(s.last_seen_at)}</div></div>
              {s.current && <span className="text-[11px] font-semibold text-emerald-700">This device</span>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export function NotificationsPage() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/notifications?limit=50").then((r) => setData(r.data)).catch(() => setData({ items: [], unread: 0 })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <LoadingState />;
  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <Card title="Notifications" subtitle={`${data.unread} unread`} testId="notifications-card"
      action={data.unread > 0 && <button onClick={readAll} data-testid="notifications-read-all" className={btnSecondary}><CheckCheck size={14} /> Mark all read</button>}>
      {data.items.length === 0 ? <EmptyState title="No notifications" message="Account, security and vendor updates will appear here." /> : (
        <ul className="divide-y divide-border" data-testid="notification-list">
          {data.items.map((n) => (
            <li key={n.id} data-testid={`notification-${n.id}`} className={`py-3 flex gap-3 ${n.is_read ? "opacity-70" : ""}`}>
              <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${n.is_read ? "bg-muted text-stone-400" : "bg-accent text-secondary"}`}><Bell size={16} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="font-semibold text-sm text-stone-900">{n.title}</span><span className="text-[10px] font-semibold tracking-wide text-stone-400">{n.type}</span></div>
                <p className="text-sm text-stone-600">{n.message}</p>
                <div className="text-xs text-stone-400 mt-1">{fmtDate(n.created_at)}</div>
              </div>
              {!n.is_read && <button onClick={() => markRead(n.id)} data-testid={`notification-read-${n.id}`} className="text-xs font-semibold text-secondary self-start">Mark read</button>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
